import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import PlayerCard from "../components/PlayerCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useGames } from "../context/GamesContext";

export default function Roster() {
  const { activeGames } = useGames();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/players")
      .then((r) => setPlayers(r.data))
      .catch(() => setPlayers([]))
      .finally(() => setLoading(false));
  }, []);

  const byGame = useMemo(() => {
    const m = {};
    for (const g of activeGames) m[g.key] = [];
    for (const p of players) {
      for (const k of Object.keys(m)) {
        if (p.game === k || (p.secondary_games || []).includes(k)) m[k].push(p);
      }
    }
    return m;
  }, [players, activeGames]);

  return (
    <div data-testid="roster-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
          The Roster
        </div>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl">
          Meet the <span className="text-axe-purple">Squad</span>
        </h1>
        <p className="mt-5 max-w-2xl text-neutral-400 text-base sm:text-lg">
          Thirteen players across three titles. Different playstyles, one standard — ruthless
          preparation and clean execution.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-12">
        <Tabs defaultValue={activeGames[0]?.key || ""} className="w-full" key={activeGames.map((g) => g.key).join(",")}>
          <TabsList
            className="bg-axe-surface border border-axe-border rounded-full p-1 h-auto flex-wrap gap-1 w-full sm:w-auto"
            data-testid="roster-tabs"
          >
            {activeGames.map((g) => (
              <TabsTrigger
                key={g.key}
                value={g.key}
                data-testid={`roster-tab-${g.key}`}
                className="rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-400 data-[state=active]:bg-axe-purple data-[state=active]:text-white data-[state=active]:drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
              >
                {g.label} · {byGame[g.key]?.length || 0}
              </TabsTrigger>
            ))}
          </TabsList>

          {activeGames.map((g) => (
            <TabsContent key={g.key} value={g.key} className="mt-8">
              {loading ? (
                <div className="text-neutral-400" data-testid="roster-loading">Loading roster…</div>
              ) : (byGame[g.key] || []).length === 0 ? (
                <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center" data-testid={`roster-empty-${g.key}`}>
                  Roster announcement coming soon.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-up">
                  {byGame[g.key].map((p) => <PlayerCard key={p.id} player={p} />)}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </div>
  );
}
